// kasir.service — guard izin Tutup Kasir. View TIDAK memanggil repo langsung untuk aksi sensitif.
// Lapis kedua ada di DB: trigger `guard_dclose_verify` (0061) + RLS `update_dclose` (0059).
import { currentUser } from '@core/session';
import { logAudit } from '@core/audit';
import { pushNotif } from '@core/notif';
import { rp } from '@domain/format';
import { selisih } from '@domain/kasir';
import * as repo from './kasir.repo';

const TUTUP_ROLES = ['Owner', 'Manager', 'Admin Finance', 'SPV', 'CS'];
const VERIF_ROLES = ['Owner', 'Manager', 'Admin Finance'];

export const bolehTutup = (): boolean => TUTUP_ROLES.includes(currentUser()?.role ?? '');
export const bolehVerif = (): boolean => VERIF_ROLES.includes(currentUser()?.role ?? '');

export async function saveClose(d: Parameters<typeof repo.saveClose>[0], tutup: boolean): Promise<void> {
  if (!bolehTutup()) throw new Error('Tidak punya izin menutup kasir.');
  if (tutup && !d.outlet) throw new Error('Pilih satu cabang dulu — satu laci fisik milik satu cabang.');
  // Sudah pernah ditutup? → ini koreksi angka, bukan penutupan baru (jangan dump ulang tiap koreksi).
  const baru = tutup && !(await repo.getClose(d.tgl, d.outlet).catch(() => null))?.closed_at;
  await repo.saveClose(d, tutup);
  if (!tutup) return;
  await logAudit({ action: 'KASIR_CLOSE', module: 'payment', refId: `${d.tgl}|${d.outlet ?? '-'}`, meta: { expect: d.expect, actual: d.actual } });
  // Auto-backup "setiap toko tutup" (Apo 19 Jul): server-only, best-effort — kegagalan backup
  // TIDAK boleh menggagalkan penutupan kasir yang sudah tersimpan.
  if (baru) void (async () => {
    try { const { runBackup } = await import('@modules/backup/backup.service'); await runBackup(false); }
    catch (e) {
      console.warn('[backup] auto-backup tutup kasir gagal:', e);
      // Jangan gagal senyap: janji "backup otomatis" harus bisa dibuktikan/ditelusuri.
      void logAudit({ action: 'BACKUP_AUTO_GAGAL', module: 'backup', refId: `${d.tgl}|${d.outlet ?? '-'}`, reason: (e as Error).message });
    }
  })();
  const dif = selisihTerhitung(d.expect, d.actual);
  if (dif !== 0) {
    await pushNotif('MANAGER', d.outlet,
      `⚠️ Tutup kasir ${d.tgl} · ${d.outlet ?? '-'} SELISIH ${rp(dif)} (${dif > 0 ? 'fisik LEBIH' : 'fisik KURANG'}) — ditutup ${currentUser()?.nama ?? '-'}. Catatan: ${d.catatan ?? '(kosong)'}`);
  }
}

/**
 * Selisih yang layak dilaporkan = HANYA metode yang benar-benar dihitung fisik.
 * Metode yang belum disentuh kasir bukan selisih, itu "belum dihitung" — kalau ikut dijumlah,
 * setiap penutupan parsial mengirim alarm palsu ke Owner dan alarm yang benar ikut diabaikan.
 */
function selisihTerhitung(expect: Record<string, number>, actual: Record<string, number>): number {
  const d = selisih(expect, actual);
  return Object.keys(actual).reduce((a, k) => a + (d[k] ?? 0), 0);
}

/**
 * Verifikasi penutupan. Pemisahan tugas: yang MENUTUP tak boleh memverifikasi sendiri —
 * kalau boleh, selisih kas bisa dikunci oleh orang yang membuatnya. Ditegakkan ulang oleh trigger DB.
 */
export async function verifyClose(tgl: string, outlet: string | null, closedBy: string | null): Promise<void> {
  if (!bolehVerif()) throw new Error('Verifikasi penutupan hanya oleh Finance/Manager/Owner.');
  const nama = currentUser()?.nama ?? '';
  // Owner dikecualikan (Apo 19 Jul): otoritas tertinggi, tak ada pihak di atasnya. Tetap teraudit.
  if (closedBy && closedBy === nama && currentUser()?.role !== 'Owner') throw new Error('Tidak boleh memverifikasi penutupan yang Anda buat sendiri.');
  await repo.verifyClose(tgl, outlet);
  await logAudit({ action: 'KASIR_VERIFY', module: 'payment', refId: `${tgl}|${outlet ?? '-'}` });
}
